import React, { FC, useEffect, useMemo, useState } from 'react'
import { Form, Spinner } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { listRiskLibraryRiskNumberOptions } from '../../../QHSE/core/_requests'
import type { RiskLibraryRiskNumberOption } from '../../../QHSE/core/_models'
import type { PmsJobRecord } from '../../core/pms/_models'
import { riskOptionMatchesSearch, shouldExcludeCriticalEquipment } from './pmsRiskAssessmentPicker'
import { RiskLibraryTaskDetailModal } from './RiskLibraryTaskDetailModal'
import { RiskLibraryBrowseModal } from './RiskLibraryBrowseModal'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

type Props = {
  job: PmsJobRecord
  applicable: boolean
  onApplicableChange: (v: boolean) => void
  value: string
  onValueChange: (v: string) => void
  errorText?: string
  onClearError?: () => void
}

export const RiskAssessmentNumberSection: FC<Props> = ({
  job,
  applicable,
  onApplicableChange,
  value,
  onValueChange,
  errorText,
  onClearError,
}) => {
  const excludeCe = shouldExcludeCriticalEquipment(job)
  const [options, setOptions] = useState<RiskLibraryRiskNumberOption[]>([])
  const [loading, setLoading] = useState(false)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [detailTaskId, setDetailTaskId] = useState<number | null>(null)
  const [browseOpen, setBrowseOpen] = useState(false)

  useEffect(() => {
    if (!applicable) {
      setOptions([])
      setLoadErr(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setLoadErr(null)
    listRiskLibraryRiskNumberOptions(excludeCe, null, job.vesselId ?? null)
      .then((rows) => {
        if (!cancelled) setOptions(rows)
      })
      .catch((e: any) => {
        if (!cancelled) {
          setLoadErr('Could not load risk assessment library')
          setOptions([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [applicable, excludeCe, job.vesselId])

  const filtered = useMemo(
    () => options.filter((o) => riskOptionMatchesSearch(o, search)),
    [options, search]
  )

  const selectedOption = useMemo(
    () => options.find((o) => o.displayNumber === value) ?? null,
    [options, value]
  )

  const legacyUnknownValue = value && !options.some((o) => o.displayNumber === value)

  const selectListSize = Math.min(12, Math.max(4, filtered.length + (legacyUnknownValue ? 2 : 1)))

  const onSelectRow = (display: string) => {
    onValueChange(display)
    onClearError?.()
  }

  return (
    <div className='border rounded p-3 mb-4 bg-white'>
      <Form.Check
        type='checkbox'
        id='ra-applicable'
        className='mb-3'
        label='Risk Assessment Number applicable for this job?'
        checked={applicable}
        onChange={(e) => {
          const on = e.target.checked
          onApplicableChange(on)
          if (!on) {
            onValueChange('')
            setSearch('')
          }
          onClearError?.()
        }}
      />
      {applicable && (
        <>
          <div className='d-flex flex-wrap gap-2 mb-2'>
            <Link
              to='/qhse/risk-assessment'
              className='btn btn-sm btn-light-primary'
              target='_blank'
              rel='noreferrer'
            >
              Open Risk Assessment library
            </Link>
            <button type='button' className='btn btn-sm btn-light-success' onClick={() => setBrowseOpen(true)}>
              Browse tasks ..
            </button>
            {selectedOption && (
              <button
                type='button'
                className='btn btn-sm btn-light-info'
                onClick={() => setDetailTaskId(selectedOption.libraryTaskId)}
              >
                View task details
              </button>
            )}
          </div>
          {value.trim().length > 0 && (
            <div className='alert alert-primary d-flex flex-wrap align-items-center gap-2 py-2 px-3 mb-3' role='status'>
              <span className='fw-bold text-dark text-uppercase fs-8 mb-0'>Selected risk assessment</span>
              <span className='badge badge-light-primary fs-6 fw-bold'>{value.trim()}</span>
              {selectedOption && <span className='text-dark small mb-0'>{selectedOption.title}</span>}
              {!loading && !loadErr && legacyUnknownValue && (
                <span className='text-dark small mb-0'>
                  (not in the current library list — value will still be saved if you complete with it)
                </span>
              )}
            </div>
          )}
          <Form.Label className='fw-semibold text-dark mb-1'>
            Risk Assessment Number <span className='text-danger'>*</span>
          </Form.Label>
          <Form.Control
            type='search'
            className='mb-2 text-dark'
            placeholder='Search RA number, title, category (e.g. RA-DR-01, DR 01, RADR0100)…'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {loading && (
            <div className='text-muted small py-2'>
              <Spinner animation='border' size='sm' className='me-2' />
              Loading library tasks…
            </div>
          )}
          {loadErr && <div className='text-danger small mb-2'>{loadErr}</div>}
          {!loading && !loadErr && (
            <>
              <SearchableSelect
                className='form-select text-dark'
                size={selectListSize}
                value={value}
                onChange={(e) => onSelectRow(e.target.value)}
              >
                <option value=''>— Select a risk assessment —</option>
                {legacyUnknownValue && (
                  <option value={value}>{value} (resolve in library if needed)</option>
                )}
                {filtered.map((o) => (
                  <option key={o.libraryTaskId} value={o.displayNumber}>
                    {o.displayNumber} — {o.title}
                  </option>
                ))}
              </SearchableSelect>
              <div className='text-muted fs-8 mt-1'>
                {excludeCe
                  ? 'Critical equipment (RA-CE) numbers are not available for this overdue scheduled job without a defect link.'
                  : 'Only the latest task version is listed. Stored value is the canonical RA number.'}
              </div>
            </>
          )}
          {errorText && <div className='text-danger fs-8 mt-1'>{errorText}</div>}
        </>
      )}
      <RiskLibraryTaskDetailModal visible={detailTaskId != null} taskId={detailTaskId} onClose={() => setDetailTaskId(null)} />
      <RiskLibraryBrowseModal
        visible={browseOpen}
        onClose={() => setBrowseOpen(false)}
        job={job}
        onPick={(display) => {
          onValueChange(display)
          setSearch('')
          onClearError?.()
        }}
      />
    </div>
  )
}
